import type { IPlayer } from "./IPlayer.ts";

import type {
    BuildingConfig,
    IBuildingsDefinition,
    IMapDefinition,
    ICombatUnit,
    INpcGroup,
    ITroopDefinition,
    IDropItemDefinition
} from "./JsonDefinitions.ts";
import type { IResources } from "./IResources.ts";
import { type IEffect, type IHero } from "./IHero.ts";
import { ICombatGroup } from "./IMarchingArmy.ts";
import { IPoint } from "./IPoint.ts";

const totemBuildingId = 19;
const altarBuildingId = 20;

export const resources = ["wood", "stone", "copper", "food"];
export const effectOrder = ["attack", "defense", "armor", "hp", "speed", "production", "population"];

export const EffectRanges: Record<number, { min: number, max: number }> = {
    1: { min: 5, max: 10 },
    2: { min: 11, max: 20 },
    3: { min: 21, max: 30 },
    4: { min: 31, max: 40 },
    5: { min: 41, max: 50 },
};

/**
 * Central calculator containing formulas for various game mechanics.
*/
export class MechanicsCalculator {
    public static MaxTrainingSlots = 4;
    public static BarracksId = 18;
    public static AltarId = 20;
    public static MaxHeroStars = 5;
    public static FireplaceId = 22;
    public static MaxHeroesCount = 7;

    private housingBuildingIds: number[] = [];
    private npcTemplates: ICombatUnit[];

    constructor(
        private buildingTemplates: IBuildingsDefinition,
        private _mapDefinition: IMapDefinition,
        private troopTemplates: ITroopDefinition[],
        private dropItemTemplates: IDropItemDefinition[]) {
        this.npcTemplates = this.troopTemplates.filter(t => t.id >= 100);
        const isHousing = (b: BuildingConfig) => b.resource == "housing";
        const arr = this.buildingTemplates.buildings.filter(isHousing).map(b => b.id);
        this.housingBuildingIds.push(...arr);
    }

    public static EffectUpgradeXpNeeded(currentLevel: number): number {
        const E_base = 5;
        const alpha = 2.4;

        const xpNeeded = E_base * Math.pow(currentLevel + 1, alpha);
        return xpNeeded;
    }

    public static IsHeroMaxedOut(hero: IHero): boolean {
        const { max } = EffectRanges[hero.stars];
        const upgradable = (hero.effectsRng ?? [])
            .map((value, idx) => ({ value, idx }))
            .filter(x => x.value < max)
            .sort((a, b) => a.value - b.value);
        return upgradable.length === 0;
    }

    public static CanAscendHero(player: IPlayer, hero: IHero): boolean {
        if (hero.stars === 5)
            return false;

        // must have maxed out effects
        const { min, max } = EffectRanges[hero.stars];
        const effectsSum = hero.effectsRng.reduce((acc, val) => acc + val, 0);
        if (effectsSum !== hero.effectsRng.length * max)
            return false;

        // how much XP is needed to bring all hero effects to the next star range
        const xpNeeded = MechanicsCalculator.EffectUpgradeXpNeeded(max + 1) * hero.effectsRng.length;
        if (hero.experience < xpNeeded)
            return false;

        //  TODO: check if players inventory has needed ascension material

        return true;
    }

    /**
     * Calculates the upgrade duration in seconds for a building to reach the next level.
     *
     * @param baseDuration - The base duration in seconds (for upgrading to level 6).
     * @param currentLevel - The current level of the building.
     * @returns The duration in seconds required to upgrade to the next level.
     */
    public static calculateUpgradeDuration(baseDuration: number, currentLevel: number): number {
        const targetLevel = currentLevel + 1;

        if (targetLevel <= 5) {
            // Levels 1-5: Built immediately
            return 0;
        } else if (targetLevel === 6) {
            // Level 6: Base duration
            return baseDuration;
        } else {
            // Levels 7 and above: (Target Level - 5) * 1.5 * Base Duration
            return (targetLevel - 5) * 1.5 * baseDuration;
        }
    }

    /**
     * Calculates the number of hero stars unlocked by the player.
     * @param player 
     * @returns 0 if no fireplace has been build, otherwise the number of unlocked hero stars.
     */
    public static getUnlockedHeroStars(player: IPlayer): { unlockedStars: number, levelForNextStar: number } {
        const fireplace = player.buildings.find((b) => b.id == MechanicsCalculator.FireplaceId);
        if (!fireplace) return { unlockedStars: 0, levelForNextStar: 15 };
        let count = Math.floor(fireplace.level / 15) + 1;
        count = count > MechanicsCalculator.MaxHeroStars ? MechanicsCalculator.MaxHeroStars : count;
        const nextStarLevel = count * 15;
        return { unlockedStars: count, levelForNextStar: count < MechanicsCalculator.MaxHeroStars ? nextStarLevel : 0 };
    }

    /**
     * Calculates the cost of recruiting a hero, the cost grows exponentially with unlocked number of stars.
     * @param player 
     * @returns 
     */
    public getHeroRecruitCost(player: IPlayer): IResources {
        const fireplace = this.buildingTemplates.buildings.find(b => b.id === MechanicsCalculator.FireplaceId)!;
        const cost = fireplace.action.cost;
        const { unlockedStars } = MechanicsCalculator.getUnlockedHeroStars(player);
        return {
            wood: cost.wood * unlockedStars * unlockedStars,
            stone: cost.stone * unlockedStars * unlockedStars,
            copper: cost.copper * unlockedStars * unlockedStars,
            food: 0,
            population: 0,
            mana: 0
        };
    }

    /**
     * Calculates available training slots, next slot unlock level and training duration bonus.
     * @param player 
     * @returns 0 if no barracks has been build, otherwise the number of unlocked training slots.
     */
    public static getUnlockedTrainingSlots(player: IPlayer): { unlockedSlots: number, levelForNextSlot: number, trainingBonus: number } {
        const maxBonus = 0.75;
        const barracks = player.buildings.find(b => b.id == MechanicsCalculator.BarracksId);
        if (!barracks) return { unlockedSlots: 0, levelForNextSlot: 15, trainingBonus: 0 };
        let count = Math.floor(barracks.level / 15) + 1;
        count = count > MechanicsCalculator.MaxTrainingSlots ? MechanicsCalculator.MaxTrainingSlots : count;
        const nextSlotLevel = count * 15;
        return {
            unlockedSlots: count,
            levelForNextSlot: count < MechanicsCalculator.MaxTrainingSlots ? nextSlotLevel : 0,
            trainingBonus: Math.min((barracks.level > 45 ? barracks.level - 45 : 0) * 0.01, maxBonus)
        };
    }

    public get buildingsDefinition() {
        return this.buildingTemplates;
    }
    public get npcDefinition() {
        return this.npcTemplates;
    }
    public get mapDefinition() {
        return this._mapDefinition;
    }
    public get dropItemDefinition() {
        return this.dropItemTemplates;
    }

    /**
     * Adds various bonuses to the player's resources production.
     * Bonuses are based on Altar level, buffs, effects, events, etc.
     * @param player
     * @returns
     */
    public calcResourceProductionBonus(player: IPlayer) {
        let productionBonus = 0;
        for (let building of player.buildings) {
            if (building.id === altarBuildingId) {
                let cfg = this.buildingTemplates.buildings.find(b => b.id === building.id)!;
                productionBonus += cfg.production * building.level;
            }
        }

        //  TODO: add bonus from hero effects, buffs, events, etc.
        return productionBonus;
    }

    /**
     * Calculates the player's resources production and consumption per minute.
     * Note: The production bonuses (buffs, effects or altar %) are included in the calculated production.
     * @param player 
     * @returns 
     */
    public calcResourcesProductionPerMinute(player: IPlayer) {
        let productionPerMinute: IResources = {
            wood: 0,
            stone: 0,
            copper: 0,
            food: 0,
            population: 1,   //  start with 1/min increase, even without any buildings
            mana: 0

        };
        let consumptionPerMinute: IResources = {
            wood: 0,
            stone: 0,
            copper: 0,
            food: 0,
            population: 0,
            mana: 0
        };

        let housingCapacity = 0;
        let manaProduction = 0;
        for (let building of player.buildings) {
            let buildingTemplate = this.buildingTemplates.buildings.find(b => b.id === building.id)!;
            if (resources.includes(buildingTemplate.resource)) {
                productionPerMinute[buildingTemplate.resource as keyof IResources] += buildingTemplate.production * building.level;
            } else if (building.id === totemBuildingId) {
                productionPerMinute.population += buildingTemplate.production * building.level;
            } else if (this.housingBuildingIds.includes(building.id)) {
                productionPerMinute.population++;
                housingCapacity += buildingTemplate.production * building.level;
            }

            if (buildingTemplate.manaStartAfter && building.level > buildingTemplate.manaStartAfter) {
                manaProduction += building.level - buildingTemplate.manaStartAfter;
            }
        }

        //  add resource production bonus (without mana)
        const resourceProductionBonus = this.calcResourceProductionBonus(player);
        if (resourceProductionBonus > 0) {
            for (let resource of Object.keys(productionPerMinute)) {
                productionPerMinute[resource as keyof IResources] += productionPerMinute[resource as keyof IResources] * resourceProductionBonus;
            }
        }
        productionPerMinute.mana = manaProduction / 60;
        const totalPopulation = player.resources.population;
        const totalUnits = player.units.reduce((acc, u) => acc + u.count, 0);
        consumptionPerMinute.food = totalPopulation + totalUnits;
        if (!this.canGrowPopulation(player.resources.population, housingCapacity, player.resources.food - consumptionPerMinute.food)) {
            productionPerMinute.population = 0;
        }

        return { productionPerMinute, consumptionPerMinute, totalPopulation, totalUnits };
    }

    /**
     * Calculates the player's total housing capacity and the number of houses.
     * @param player 
     * @returns 
     */
    public calcHousings(player: IPlayer) {
        let numberOfHouses = 0;
        let housingCapacity = 0;
        player.buildings.filter(b => this.housingBuildingIds.includes(b.id)).forEach(b => {
            const buildingTemplate = this.buildingTemplates.buildings.find(bc => bc.id == b.id) as BuildingConfig;
            housingCapacity += buildingTemplate.production * b.level;
            numberOfHouses++;
        });
        return { numberOfHouses, housingCapacity };
    }

    public calcMaxStorageCapacity(player: IPlayer): number {
        const storingConfig = this.buildingTemplates.buildings.find(b => b.resource == "storage") as BuildingConfig;
        const playerStorage = player.buildings.find(b => b.id == storingConfig.id);
        let maxCapacity = this.buildingTemplates.playerConst.baseMaxCapacity;
        if (playerStorage) {
            maxCapacity += storingConfig.production * playerStorage.level;
        }

        return maxCapacity;
    }

    /**
     * Checks if the player can grow the population: housing capacity must be greater than population and food must be greater than population.
     * 
     * @param population current population
     * @param housingCapacity current housing capacity
     * @param food current food reduced by the population and troops consumption
     * @returns 
     */
    public canGrowPopulation(population: number, housingCapacity: number, food: number): boolean {
        return housingCapacity > population && food >= population;
    }

    public calcNpcPower(npc: ICombatUnit): number {
        return npc.stats.armor + npc.stats.attack + npc.stats.defense + npc.stats.hp;
    }

    public calcNpcGroupPower(npcGroups: INpcGroup[]): number {
        let totalPower = 0;
        npcGroups.forEach(group => {
            const npc = this.npcTemplates.find(n => n.id == group.id)!;
            totalPower += this.calcNpcPower(npc) * group.count;
        });
        return totalPower;
    }

    /**
     * Checks if the player has enough resources and population to train a troop.
     * @param troop 
     * @param numberOfTroops 
     * @param resource 
     * @returns 
     */
    public canTrainTroop(troop: ITroopDefinition, numberOfTroops: number, resource: IResources): boolean {
        return resource.wood >= numberOfTroops * troop.trainingCost.wood &&
            resource.stone >= numberOfTroops * troop.trainingCost.stone &&
            resource.copper >= numberOfTroops * troop.trainingCost.copper &&
            resource.food >= numberOfTroops * troop.trainingCost.food &&
            resource.population >= numberOfTroops * troop.trainingCost.population;
    }

    /**
     * Return the first N unlocked effects in the SAME order as hero.effects,
     * where N = hero.stars. Each unlocked effect value = base + rng[i].
     */
    public static getUnlockedHeroEffects(hero: IHero | undefined): IEffect[] {
        if (!hero) return [];

        const effects = hero.effects ?? [];
        const rng = hero.effectsRng ?? [];
        const unlockedCount = Math.min(hero.stars ?? 0, effects.length);

        const unlocked: IEffect[] = [];
        for (let i = 0; i < unlockedCount; i++) {
            const base = effects[i];
            const bonus = rng[i] ?? 0; // align by index
            unlocked.push({ type: base.type, value: base.value + bonus });
        }
        return unlocked;
    }

    public getMarchSpeedAndDuration(player: IPlayer, hero: IHero, target: IPoint, combatGroups: ICombatGroup[]) {
        let slowestTroopSpeed = 100;
        for (let group of combatGroups) {
            if (group.count > 0) {
                const troop = this.troopTemplates.find(t => t.id === group.troopId)!;
                if (troop.stats.speed < slowestTroopSpeed) {
                    slowestTroopSpeed = troop.stats.speed;
                }
            }
        }

        //  apply speed effect bonus, the effect is in %
        const speedEffectType = "speed";
        const heroEffects = MechanicsCalculator.getUnlockedHeroEffects(hero);
        const speedEffect = heroEffects.find(e => e.type === speedEffectType);
        if (speedEffect) {
            const pct = 1 + (speedEffect.value / 100);
            console.log(`createMarchingTroops() Hero ${hero.name} has speed effect of ${speedEffect.value}%, slowest troop speed is ${slowestTroopSpeed}, pct: ${pct}, new speed: ${slowestTroopSpeed * pct}`);
            slowestTroopSpeed = slowestTroopSpeed * pct; //Math.ceil(slowestTroopSpeed * pct);
        }
        const dx = target.x - player.villagePosition.x;
        const dy = target.y - player.villagePosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const timeToTarget = distance / slowestTroopSpeed * 1000;
        return { speed: slowestTroopSpeed, duration: timeToTarget };
    }
}